import axios from 'axios'
import { useAdminStore } from '../store/adminStore'

// Operator calls use the same base URL, authenticated with the admin JWT
const operatorClient = axios.create({ baseURL: '/api/v1' })

// A 401 means the admin session is gone (expired or revoked) -- without
// this, each call site rendered whatever empty/error state its own query
// fell into (e.g. a device list showing "none registered" when the real
// cause was an expired token), instead of sending the user back to log
// in. AdminLayout only checks the JWT's exp once on mount; it never
// reacts to a live 401 from a call made later in the same session. One
// interceptor here covers every operatorApi call site at once, the way
// AdminUsers.tsx's handleUnauth() otherwise has to be remembered to call
// per fetch(). A hard redirect (not react-router's navigate, which isn't
// reachable from here outside a component) also guarantees no stale
// query data or component state lingers from before the logout.
operatorClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      useAdminStore.getState().clearSession()
      window.location.href = '/admin/login'
    }
    return Promise.reject(error)
  },
)

function headers(token: string) {
  return { Authorization: `Bearer ${token}` }
}

export interface Tenant {
  id: string
  name: string
  municipality: string
  contact_email: string | null
  seats: number
  license_issued_at: string | null
  license_expires_at: string | null
  active: boolean
  modules: string[]
  created_at: string
  is_demo: boolean
  is_template: boolean
}

export interface TenantCreate {
  name: string
  municipality: string
  contact_email?: string
  contact_phone?: string
  seats: number
  license_expires_at?: string
  modules?: string[]
}

export interface TenantUpdate {
  name?: string
  municipality?: string
  contact_email?: string
  modules?: string[]
  seats?: number
  license_expires_at?: string
  active?: boolean
}

export interface TenantStats {
  users_total: number
  users_activated: number
  seats_total: number
  seats_used: number
}

export interface ExtendedToken {
  activation_token: string
  qr_data: string
}

export interface ImportError {
  row: number
  message: string
}

export interface ImportedAccount {
  email: string
  activation_token: string
  qr_data: string
}

export interface UsersImportResult {
  imported: number
  skipped: number
  errors: ImportError[]
  accounts: ImportedAccount[]
}

export interface ImportResult {
  imported: number
  skipped: number
  errors: ImportError[]
}

export interface ProvisionedAccount {
  user_id: string
  activation_token: string
  label: string | null
  roles: string[]
  qr_data: string
}

export interface AccountSpec {
  roles: string[]
  label?: string
}

export interface TenantUser {
  id: string
  email: string | null
  full_name: string | null
  roles: string[]
  is_placeholder: boolean
  active: boolean
  activated_at: string | null
  created_at: string
  notify_scada: boolean
  is_blocked: boolean
  is_director: boolean
}

export interface DeviceInfo {
  id: string
  device_name: string | null
  platform: string | null
  app_version: string | null
  last_sync_at: string | null
  created_at: string
}

export interface ScadaDevice {
  id: string
  device_key: string
  node_type: string
  label: string
  active: boolean
  alert_offline_minutes: number | null
  alert_stopped_minutes: number | null
}

export type ConfigTier = 'base' | 'extended' | 'deploy'

export interface EdgeNode {
  id: string
  name: string
  ingress_key: string
  active: boolean
  config_tier: ConfigTier
  created_at: string
  last_seen_at: string | null
  last_seen_ip: string | null
}

export interface DeploymentLogEntry {
  id: string
  captured_at: string
  received_at: string
  level: string
  logger: string
  device_key: string | null
  message: string
}

export interface EdgeFile {
  id: string
  filename: string
  size_bytes: number
  received_at: string
  expires_at: string
}

export interface ShellTicket {
  ticket: string
  expires_at: number
}

export const operatorApi = {
  listTenants: (key: string) =>
    operatorClient.get<Tenant[]>('/operator/tenants', { headers: headers(key) }),

  createTenant: (key: string, body: TenantCreate) =>
    operatorClient.post<{ tenant_id: string }>('/operator/tenants', body, { headers: headers(key) }),

  updateTenant: (key: string, tenantId: string, body: TenantUpdate) =>
    operatorClient.patch<{ ok: boolean }>(`/operator/tenants/${tenantId}`, body, { headers: headers(key) }),

  provisionAccounts: (key: string, tenantId: string, accounts: AccountSpec[]) =>
    operatorClient.post<ProvisionedAccount[]>(
      `/operator/tenants/${tenantId}/accounts`,
      { accounts },
      { headers: headers(key) },
    ),

  listUsers: (key: string, tenantId: string) =>
    operatorClient.get<TenantUser[]>(`/operator/tenants/${tenantId}/users`, { headers: headers(key) }),

  listUserDevices: (key: string, tenantId: string, userId: string) =>
    operatorClient.get<DeviceInfo[]>(`/operator/tenants/${tenantId}/users/${userId}/devices`, { headers: headers(key) }),

  resetPassword: (key: string, tenantId: string, userId: string, newPassword: string) =>
    operatorClient.post(
      `/operator/tenants/${tenantId}/users/${userId}/reset-password`,
      { new_password: newPassword },
      { headers: headers(key) },
    ),

  revokeSessions: (key: string, tenantId: string, userId: string) =>
    operatorClient.post(
      `/operator/tenants/${tenantId}/users/${userId}/revoke-sessions`,
      {},
      { headers: headers(key) },
    ),

  blockUser: (key: string, tenantId: string, userId: string) =>
    operatorClient.put(
      `/operator/tenants/${tenantId}/users/${userId}/block`,
      {},
      { headers: headers(key) },
    ),

  unblockUser: (key: string, tenantId: string, userId: string) =>
    operatorClient.put(
      `/operator/tenants/${tenantId}/users/${userId}/unblock`,
      {},
      { headers: headers(key) },
    ),

  setDirector: (key: string, tenantId: string, userId: string, isDirector: boolean) =>
    operatorClient.put(
      `/operator/tenants/${tenantId}/users/${userId}/director`,
      { is_director: isDirector },
      { headers: headers(key) },
    ),

  /** Returns a URL to trigger the PDF download in a new tab (token passed as query param) */
  qrBundleUrl: (tenantId: string, token = '') =>
    token
      ? `/api/v1/operator/tenants/${tenantId}/qr-bundle?token=${encodeURIComponent(token)}`
      : `/api/v1/operator/tenants/${tenantId}/qr-bundle`,

  deleteTenant: (key: string, tenantId: string, confirmName: string) =>
    operatorClient.delete<{ ok: boolean }>(`/operator/tenants/${tenantId}`, {
      headers: headers(key),
      data: { confirm_name: confirmName },
    }),

  tenantStats: (key: string, tenantId: string) =>
    operatorClient.get<TenantStats>(`/operator/tenants/${tenantId}/stats`, { headers: headers(key) }),

  extendToken: (key: string, tenantId: string, userId: string) =>
    operatorClient.post<ExtendedToken>(
      `/operator/tenants/${tenantId}/users/${userId}/extend-token`,
      {},
      { headers: headers(key) },
    ),

  importUsers: (key: string, tenantId: string, file: File) => {
    const fd = new FormData(); fd.append('file', file)
    return operatorClient.post<UsersImportResult>(
      `/operator/tenants/${tenantId}/import/users`, fd, { headers: headers(key) },
    )
  },

  importMeters: (key: string, tenantId: string, file: File) => {
    const fd = new FormData(); fd.append('file', file)
    return operatorClient.post<ImportResult>(
      `/operator/tenants/${tenantId}/import/meters`, fd, { headers: headers(key) },
    )
  },

  importScada: (key: string, tenantId: string, file: File) => {
    const fd = new FormData(); fd.append('file', file)
    return operatorClient.post<ImportResult>(
      `/operator/tenants/${tenantId}/import/scada`, fd, { headers: headers(key) },
    )
  },

  listEdgeNodes: (key: string, tenantId: string) =>
    operatorClient.get<EdgeNode[]>(`/operator/tenants/${tenantId}/edge-nodes`, { headers: headers(key) }),

  createEdgeNode: (key: string, tenantId: string, name: string) =>
    operatorClient.post<EdgeNode>(
      `/operator/tenants/${tenantId}/edge-nodes`,
      { name },
      { headers: headers(key) },
    ),

  regenerateEdgeKey: (key: string, tenantId: string, nodeId: string) =>
    operatorClient.post<EdgeNode>(
      `/operator/tenants/${tenantId}/edge-nodes/${nodeId}/regenerate-key`,
      {},
      { headers: headers(key) },
    ),

  updateEdgeNode: (key: string, tenantId: string, nodeId: string, body: { name?: string; active?: boolean; config_tier?: ConfigTier }) =>
    operatorClient.patch<EdgeNode>(
      `/operator/tenants/${tenantId}/edge-nodes/${nodeId}`,
      body,
      { headers: headers(key) },
    ),

  deleteEdgeNode: (key: string, tenantId: string, nodeId: string) =>
    operatorClient.delete<{ ok: boolean }>(
      `/operator/tenants/${tenantId}/edge-nodes/${nodeId}`,
      { headers: headers(key) },
    ),

  edgeConfigUrl: (tenantId: string, nodeId: string, token = '') =>
    token
      ? `/api/v1/operator/tenants/${tenantId}/edge-nodes/${nodeId}/config?token=${encodeURIComponent(token)}`
      : `/api/v1/operator/tenants/${tenantId}/edge-nodes/${nodeId}/config`,

  edgePackageUrl: (tenantId: string, nodeId: string, token = '') =>
    token
      ? `/api/v1/operator/tenants/${tenantId}/edge-nodes/${nodeId}/package?token=${encodeURIComponent(token)}`
      : `/api/v1/operator/tenants/${tenantId}/edge-nodes/${nodeId}/package`,

  setUserNotifyScada: (key: string, tenantId: string, userId: string, enabled: boolean) =>
    operatorClient.patch<{ ok: boolean }>(
      `/scada/tenants/${tenantId}/users/${userId}/notify-scada`,
      { notify_scada: enabled },
      { headers: headers(key) },
    ),

  listScadaDevices: (key: string, tenantId: string) =>
    operatorClient.get<ScadaDevice[]>(`/operator/tenants/${tenantId}/scada-devices`, { headers: headers(key) }),

  setDeviceThresholds: (
    key: string,
    tenantId: string,
    deviceKey: string,
    thresholds: { alert_offline_minutes?: number | null; alert_stopped_minutes?: number | null },
  ) =>
    operatorClient.patch<{ ok: boolean }>(
      `/operator/tenants/${tenantId}/scada-devices/${deviceKey}/thresholds`,
      thresholds,
      { headers: headers(key) },
    ),

  listDeploymentLogs: (key: string, tenantId: string, nodeId: string, opts?: { limit?: number; before?: string }) =>
    operatorClient.get<DeploymentLogEntry[]>(
      `/operator/tenants/${tenantId}/edge-nodes/${nodeId}/deployment-logs`,
      { headers: headers(key), params: opts },
    ),

  listEdgeFiles: (key: string, tenantId: string, nodeId: string) =>
    operatorClient.get<EdgeFile[]>(
      `/operator/tenants/${tenantId}/edge-nodes/${nodeId}/files`,
      { headers: headers(key) },
    ),

  /** Plain text body; fetched as a blob because a bare link can't carry the operator header. */
  downloadEdgeFile: (key: string, tenantId: string, nodeId: string, fileId: string) =>
    operatorClient.get<Blob>(
      `/operator/tenants/${tenantId}/edge-nodes/${nodeId}/files/${fileId}`,
      { headers: headers(key), responseType: 'blob' },
    ),

  createShellTicket: (key: string, tenantId: string, nodeId: string) =>
    operatorClient.post<ShellTicket>(
      `/operator/tenants/${tenantId}/edge-nodes/${nodeId}/shell/token`,
      {},
      { headers: headers(key) },
    ),

  /** Browser-relay WebSocket URL for a deploy-tier shell session (ticket already minted via createShellTicket) */
  shellWebSocketUrl: (tenantId: string, nodeId: string, ticket: string) => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${wsProtocol}//${window.location.host}/api/v1/operator/tenants/${tenantId}/edge-nodes/${nodeId}/shell?ticket=${encodeURIComponent(ticket)}`
  },
}

// LocalStorage key for the operator API key
export const OPERATOR_KEY_STORAGE = 'operator-api-key'
